import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { listArticles } from "@/lib/api/content";

/**
 * Remove sinais herdados do HTML estático servido pelo fallback da hospedagem
 * (canonical/og:url da home) e garante noindex, mesmo fora do Helmet.
 */
function useStrip404Signals() {
  useEffect(() => {
    document.querySelectorAll('link[rel="canonical"], meta[property="og:url"]').forEach((el) => el.remove());
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = "noindex, follow";
  });
}

/** Página 404 com head próprio (noindex, follow), sem canonical. */
export function NotFoundHead() {
  useStrip404Signals();
  return (
    <Helmet>
      <title>Página não encontrada</title>
      <meta name="robots" content="noindex, follow" />
      <meta name="googlebot" content="noindex, follow" />
      <meta name="description" content="A página que você procura não existe ou foi movida." />
    </Helmet>
  );
}

export function NotFoundView() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [recent, setRecent] = useState<{ slug: string; title: string }[]>([]);

  useEffect(() => {
    listArticles({ page: 1 })
      .then((r) => setRecent((r.data as any[]).slice(0, 5)))
      .catch(() => setRecent([]));
  }, []);

  return (
    <div className="flex min-h-[70vh] items-center justify-center py-16">
      <NotFoundHead />
      <div className="w-full max-w-xl px-6 text-center">
        <p className="mb-2 font-display text-5xl text-foreground">404</p>
        <h1 className="mb-3 font-display text-3xl font-normal text-foreground">Página não encontrada</h1>
        <p className="mb-6 text-base text-foreground/70">
          O conteúdo pode ter sido movido ou removido. Tente buscar ou veja os posts mais recentes.
        </p>

        <form
          role="search"
          className="mb-6 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (q.trim()) navigate(`/blog?q=${encodeURIComponent(q.trim())}`);
          }}
        >
          <input
            type="search"
            aria-label="Buscar no blog"
            placeholder="Buscar no blog…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
          />
          <button type="submit" className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
            Buscar
          </button>
        </form>

        <div className="mb-8 flex justify-center gap-6 text-sm font-semibold">
          <Link to="/" className="text-primary underline">Página inicial</Link>
          <Link to="/blog" className="text-primary underline">Blog</Link>
        </div>

        {recent.length > 0 && (
          <div className="text-left">
            <h2 className="mb-3 text-lg font-semibold text-foreground">Posts recentes</h2>
            <ul className="space-y-2">
              {recent.map((a) => (
                <li key={a.slug}>
                  <Link to={`/artigo/${a.slug.trim()}`} className="text-foreground/80 hover:text-primary hover:underline">
                    {a.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default NotFoundView;
